import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfig } from "@/context/ConfigContext";

const fmt = (n, loc) => Number(n).toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 3 });

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl mt-8 mb-2 border-l-4 border-[var(--pc-red)] pl-3">{children}</h2>
);

function ContentES() {
  const { precio_km_extra, precio_combustible_litro } = useConfig();
  return (
    <>
      <H2>1. Objeto</H2>
      <p>
        Los presentes términos regulan el servicio de <strong>alquiler y suscripción de vehículos</strong>{" "}
        (incluido el mini-renting Pilla3) ofrecido por <strong>PILLAUNCOCHE S.L.</strong> (CIF B-88107685), en
        adelante "pillauncoche", a través del sitio <strong>pillauncoche.com</strong>. La reserva y el pago del
        servicio implican la aceptación plena e incondicional de estos términos.
      </p>

      <H2>2. Requisitos del conductor</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Edad mínima: <strong>21 años</strong>.</li>
        <li>Antigüedad mínima del permiso de conducir: <strong>2 años</strong>.</li>
        <li>DNI / NIE / pasaporte vigente y permiso de conducir vigente (foto anverso y reverso obligatoria en el checkout).</li>
        <li>Conductor adicional: opcional, con recargo indicado en la reserva. Debe cumplir los mismos requisitos.</li>
      </ul>

      <H2>3. Reserva, pago y cancelación</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Las reservas se realizan online y se confirman por email tras el cobro (Stripe o PayPal).</li>
        <li>La aceptación de la reserva no será firme hasta la validación de los documentos aportados.</li>
        <li><strong>Cancelación gratuita</strong> hasta 24 horas antes de la fecha de recogida (reembolso íntegro).</li>
        <li>Cancelaciones dentro de las 24 horas previas o "no show": el importe queda como <strong>bono</strong> canjeable en un plazo de 12 meses.</li>
        <li>Modificaciones sujetas a disponibilidad y sin coste si se solicitan con más de 48 horas de antelación.</li>
      </ul>

      <H2>4. Tarifa, kilometraje y combustible</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>La tarifa por día incluye <strong>200 km/día</strong>. Los kilómetros excedentes se facturan a <strong>{fmt(precio_km_extra, "es-ES")} €/km</strong>.</li>
        <li>El coche se entrega con un nivel de combustible determinado y debe devolverse con el mismo. En caso contrario se factura el combustible faltante a <strong>{fmt(precio_combustible_litro, "es-ES")} €/litro</strong> + <strong>10 € de servicio</strong>.</li>
        <li>Los packs de kilómetros y los extras (silla infantil, GPS, conductor adicional) se contratan y facturan en el checkout.</li>
      </ul>

      <H2>5. Seguro y franquicia</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Todos los alquileres incluyen <strong>seguro a terceros ampliado, robo e incendio, cristales y asistencia en carretera 24 h</strong>.</li>
        <li>Franquicia estándar: <strong>500 €</strong>. Puede reducirse contratando la cobertura opcional en el checkout.</li>
        <li>Quedan excluidos: daños al chasis, techo o bajos por conducción negligente, daños causados bajo los efectos del alcohol/drogas, y usos comerciales no declarados (Uber, Cabify, reparto, escuela de conducción, competición).</li>
      </ul>

      <H2>6. Entrega y devolución</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Entrega y recogida en la ubicación indicada por el cliente dentro del área operativa de Madrid.</li>
        <li>El coche se entrega revisado y limpio. Cualquier daño preexistente se documenta con fotos firmadas por ambas partes.</li>
        <li>Se aplica un <strong>margen de seguridad entre alquileres</strong> (configurable, por defecto 2 h) para el saneamiento del vehículo.</li>
        <li>Retrasos en la devolución: se factura una hora adicional por cada fracción de hora de retraso. Retrasos superiores a 4 h se facturan como día extra completo.</li>
      </ul>

      <H2>7. Documentación y uso del vehículo</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Está prohibido: circular fuera de la UE sin autorización previa por escrito, transportar mercancías peligrosas, subarrendar el vehículo, participar en competiciones o rallies.</li>
        <li>El cliente es responsable de las multas de tráfico, aparcamiento y peajes durante el periodo de alquiler. Se repercute un cargo administrativo de <strong>25 €</strong> por gestión de multa.</li>
        <li>En caso de siniestro, el cliente debe rellenar el parte amigable y contactar con pillauncoche en un máximo de 24 h.</li>
      </ul>

      <H2>8. Fianza</H2>
      <p>
        En vehículos premium o alquileres largos, pillauncoche puede solicitar una <strong>fianza en garantía</strong>{" "}
        (preautorización sobre tarjeta) que se libera automáticamente tras la devolución del coche en buen estado.
      </p>

      <H2>9. Pilla3 (mini-renting)</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Contratación por 3 meses cerrados. Cancelaciones anticipadas suponen la pérdida del 30 % del importe pendiente.</li>
        <li>Cuotas Esencial (550 €/mes · 3.000 km totales) y Plus (600 €/mes · 6.000 km totales). Km excedentes a 0,15 €/km.</li>
        <li>Incluye seguro, mantenimiento programado, averías cubiertas por garantía e impuestos.</li>
      </ul>

      <H2>10. Facturación</H2>
      <p>
        Emitimos factura oficial con IVA (21 %) por cada reserva completada. Puedes descargarla desde tu área de
        cliente. Si no gestionas manualmente el envío, la enviamos automáticamente al email registrado{" "}
        <strong>48 h después</strong> de la devolución (plazo configurable).
      </p>

      <H2>11. Responsabilidad</H2>
      <p>
        pillauncoche no será responsable de daños indirectos derivados del uso o imposibilidad de uso del vehículo
        (pérdida de vuelo, citas incumplidas, lucro cesante). La responsabilidad máxima frente al cliente se limita
        al importe total facturado por la reserva.
      </p>

      <H2>12. Modificaciones</H2>
      <p>
        pillauncoche puede modificar estos términos en cualquier momento. La versión vigente en el momento de la
        reserva es la que aplica al contrato.
      </p>

      <H2>13. Jurisdicción y ley aplicable</H2>
      <p>
        Estos términos se rigen por la legislación española. Cualquier controversia se someterá a los{" "}
        <strong>Juzgados y Tribunales de Madrid</strong>, salvo que el cliente sea consumidor y la ley obligue a
        otro fuero.
      </p>

      <p className="text-xs text-neutral-500 border-t border-neutral-200 pt-4 mt-8">
        Última actualización: 03/07/2026. Documento orientativo; para uso en producción se recomienda validación
        por asesor legal.
      </p>
    </>
  );
}

function ContentEN() {
  const { precio_km_extra, precio_combustible_litro } = useConfig();
  return (
    <>
      <H2>1. Purpose</H2>
      <p>
        These terms govern the <strong>vehicle rental and subscription service</strong>{" "}
        (including the Pilla3 mini-lease) offered by <strong>PILLAUNCOCHE S.L.</strong> (Tax ID B-88107685), hereinafter
        "pillauncoche", through <strong>pillauncoche.com</strong>. Booking and paying for the service implies full and
        unconditional acceptance of these terms.
      </p>

      <H2>2. Driver requirements</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Minimum age: <strong>21 years old</strong>.</li>
        <li>Minimum driving licence seniority: <strong>2 years</strong>.</li>
        <li>Valid national ID / NIE / passport and a valid driving licence (front and back photo required at checkout).</li>
        <li>Additional driver: optional, with a surcharge shown at booking. Must meet the same requirements.</li>
      </ul>

      <H2>3. Booking, payment and cancellation</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Bookings are made online and confirmed by email after payment (Stripe or PayPal).</li>
        <li>The booking is not final until the submitted documents have been validated.</li>
        <li><strong>Free cancellation</strong> up to 24 hours before the pick-up date (full refund).</li>
        <li>Cancellations within 24 hours of pick-up, or a "no show": the amount is kept as a <strong>voucher</strong>, redeemable within 12 months.</li>
        <li>Changes are subject to availability and free of charge if requested more than 48 hours in advance.</li>
      </ul>

      <H2>4. Rate, mileage and fuel</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>The daily rate includes <strong>200 km/day</strong>. Extra kilometres are billed at <strong>€{fmt(precio_km_extra, "en-US")}/km</strong>.</li>
        <li>The car is delivered with a set fuel level and must be returned with the same level. Otherwise, the missing fuel is billed at <strong>€{fmt(precio_combustible_litro, "en-US")}/litre</strong> plus a <strong>€10 service fee</strong>.</li>
        <li>Kilometre packs and extras (child seat, GPS, additional driver) are contracted and billed at checkout.</li>
      </ul>

      <H2>5. Insurance and deductible</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>All rentals include <strong>extended third-party liability insurance, theft and fire, glass cover, and 24h roadside assistance</strong>.</li>
        <li>Standard deductible: <strong>€500</strong>. It can be reduced by purchasing the optional coverage at checkout.</li>
        <li>Excluded: damage to the chassis, roof or underbody caused by negligent driving, damage caused under the influence of alcohol/drugs, and undeclared commercial use (Uber, Cabify, delivery, driving school, racing).</li>
      </ul>

      <H2>6. Pick-up and return</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Pick-up and drop-off at the location indicated by the customer within the Madrid operating area.</li>
        <li>The car is delivered inspected and clean. Any pre-existing damage is documented with photos signed by both parties.</li>
        <li>A <strong>safety buffer between rentals</strong> applies (configurable, 2 hours by default) for vehicle servicing.</li>
        <li>Late returns: one additional hour is billed for each fraction of an hour of delay. Delays over 4 hours are billed as a full extra day.</li>
      </ul>

      <H2>7. Documentation and vehicle use</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Prohibited: driving outside the EU without prior written authorisation, transporting dangerous goods, subletting the vehicle, and taking part in races or rallies.</li>
        <li>The customer is responsible for traffic, parking and toll fines incurred during the rental period. An administrative fee of <strong>€25</strong> applies per fine processed.</li>
        <li>In the event of an accident, the customer must fill in the accident report form ("parte amistoso") and contact pillauncoche within a maximum of 24 hours.</li>
      </ul>

      <H2>8. Security deposit</H2>
      <p>
        For premium vehicles or long rentals, pillauncoche may request a <strong>security deposit</strong>{" "}
        (a pre-authorisation on the card) which is automatically released after the car is returned in good condition.
      </p>

      <H2>9. Pilla3 (mini-lease)</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Fixed 3-month term. Early cancellation results in the loss of 30% of the outstanding amount.</li>
        <li>Plans: Essential (€550/month · 3,000 km total) and Plus (€600/month · 6,000 km total). Extra kilometres at €0.15/km.</li>
        <li>Includes insurance, scheduled maintenance, breakdowns covered under warranty, and taxes.</li>
      </ul>

      <H2>10. Invoicing</H2>
      <p>
        We issue an official invoice including VAT (21%) for each completed booking. You can download it from your
        customer area. If not sent manually, it is sent automatically to the registered email{" "}
        <strong>48 hours</strong> after the return (this period is configurable).
      </p>

      <H2>11. Liability</H2>
      <p>
        pillauncoche is not liable for indirect damages arising from the use or inability to use the vehicle
        (missed flights, missed appointments, loss of profit). Our maximum liability towards the customer is
        limited to the total amount invoiced for the booking.
      </p>

      <H2>12. Changes</H2>
      <p>
        pillauncoche may amend these terms at any time. The version in force at the time of booking is the one
        that applies to the contract.
      </p>

      <H2>13. Governing law and jurisdiction</H2>
      <p>
        These terms are governed by Spanish law. Any dispute will be submitted to the{" "}
        <strong>Courts of Madrid</strong>, unless the customer is a consumer and the law requires a different
        jurisdiction.
      </p>

      <p className="text-xs text-neutral-500 border-t border-neutral-200 pt-4 mt-8">
        Last updated: 03/07/2026. Informational document; professional legal review is recommended for production use.
      </p>
    </>
  );
}

export default function Terminos() {
  const { i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");

  return (
    <section className="section-light py-16" data-testid="terminos-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="divider-red mb-4"></div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">{isEn ? "Terms of Service" : "Términos del Servicio"}</h1>
        <p className="text-xs uppercase tracking-widest text-neutral-500 mb-8">
          {isEn ? "Official document · pillauncoche S.L." : "Documento oficial · pillauncoche S.L."}
        </p>

        <article className="prose prose-neutral max-w-none text-base leading-relaxed text-neutral-800 space-y-5">
          {isEn ? <ContentEN /> : <ContentES />}
        </article>

        <div className="mt-10 flex justify-center">
          <Link to="/" className="btn-red px-8 py-3 text-sm uppercase tracking-widest" data-testid="terminos-accept-btn">
            {isEn ? "Accept and Return Home" : "Aceptar y Volver al Inicio"}
          </Link>
        </div>
      </div>
    </section>
  );
}
